import { useState, useEffect, useCallback } from 'react';
import { CONFIG } from '../constants/config';
import { formatDateEST, isFlagDataStale } from '../utils/dateHelpers';
import { getFromStorage, setToStorage } from '../utils/localStorage';
import { parseFacebookFlagPost, isFacebookPostToday } from '../utils/flagParser';
import flagService from '../services/flagService';

export const useBeachFlag = () => {
  const [flagStatus, setFlagStatus] = useState({
    color: '🟡',
    text: 'Yellow - Medium hazard',
    lastUpdated: formatDateEST(new Date()),
    details: 'Moderate surf and currents',
    lastChecked: Date.now()
  });
  const [showFlagUpdatePrompt, setShowFlagUpdatePrompt] = useState(false);
  const [isCheckingFlag, setIsCheckingFlag] = useState(false);

  // Load flag status from localStorage
  const loadFlagStatus = () => {
    const stored = getFromStorage(CONFIG.FLAG_CACHE_KEY);
    if (stored && stored.color && stored.lastChecked) {
      setFlagStatus(stored);
      return stored;
    }
    return null;
  };

  // Save flag status to localStorage
  const saveFlagStatus = (status) => {
    const dataToSave = {
      ...status,
      lastChecked: Date.now()
    };
    setToStorage(CONFIG.FLAG_CACHE_KEY, dataToSave);
    setFlagStatus(dataToSave);
  };

  // Automatically check for flag updates from multiple sources
  const checkFlagUpdates = useCallback(async () => {
    if (isCheckingFlag) return;
    
    setIsCheckingFlag(true);
    try {
      // Use the flag service to check multiple sources
      const flagData = await flagService.fetchCurrentFlags();
      
      if (flagData && flagData.color) {
        saveFlagStatus(flagData);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking flag updates:', error);
      return false;
    } finally {
      setIsCheckingFlag(false);
    }
  }, [isCheckingFlag]);

  // Update flag status
  const updateFlagStatus = (color, text, details) => {
    const newStatus = {
      color,
      text,
      lastUpdated: formatDateEST(new Date()),
      details,
      lastChecked: Date.now()
    };
    saveFlagStatus(newStatus);
    setShowFlagUpdatePrompt(false);
  };

  useEffect(() => {
    // Load flag status on mount
    const storedFlag = loadFlagStatus();
    
    // Check if flag data is stale
    if (!storedFlag || isFlagDataStale(storedFlag.lastChecked, CONFIG.BEACH_HOURS)) {
      // Instead of showing prompt, try to auto-update
      checkFlagUpdates();
    }
    
    // Set up periodic flag check
    const flagCheckInterval = setInterval(() => {
      const currentFlag = loadFlagStatus();
      if (!currentFlag || isFlagDataStale(currentFlag.lastChecked, CONFIG.BEACH_HOURS)) {
        // Auto-check instead of prompting
        checkFlagUpdates();
      }
    }, CONFIG.FLAG_CHECK_INTERVAL);
    
    return () => clearInterval(flagCheckInterval);
  }, [checkFlagUpdates]);

  return {
    flagStatus,
    showFlagUpdatePrompt,
    setShowFlagUpdatePrompt,
    updateFlagStatus
  };
};

export default useBeachFlag;